/**
 * useDeployedApproaches.ts
 *
 * Hook for fetching deployed approaches for a sphere, partitioned into
 * direct deployments (on the sphere itself) and inherited deployments
 * (from ancestor spheres).
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { ApiClients } from '../context/ApiClientsContext';

/** Shape of a single deployed approach returned by the graph-service endpoint */
export interface DeployedApproachInfo {
  deploymentId: string;
  sphereId: string;
  approachId: string;
  approachSystemName: string;
  approachName: Record<string, string>;
  approachVersion: number;
  deploymentStatus: string;
  deployedAt: string;
  definition?: Record<string, unknown>;
  applicability?: Record<string, unknown> | null;
}

/** Response from GET /approaches/deployments/:sphereId?includeAncestors=true */
interface DeploymentsResponse {
  deployments: DeployedApproachInfo[];
  directSphereId?: string;
}

/** Return value of the useDeployedApproaches hook */
export interface UseDeployedApproachesReturn {
  directDeployments: DeployedApproachInfo[];
  inheritedDeployments: DeployedApproachInfo[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches deployed approaches for a sphere, splitting results into
 * direct (sphere's own) and inherited (from ancestors) deployments.
 *
 * @param apiClients - API clients (pass null if not ready)
 * @param sphereId - Sphere to fetch deployments for (null/undefined skips fetch)
 */
export function useDeployedApproaches(
  apiClients: ApiClients | null,
  sphereId: string | null | undefined
): UseDeployedApproachesReturn {
  const [directDeployments, setDirectDeployments] = useState<DeployedApproachInfo[]>([]);
  const [inheritedDeployments, setInheritedDeployments] = useState<DeployedApproachInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isMountedRef = useRef(true);
  const refetchRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchDeployments = useCallback(async () => {
    if (!apiClients?.graphClient || !sphereId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await apiClients.graphClient.get<DeploymentsResponse>(
        `/approaches/deployments/${encodeURIComponent(sphereId)}`,
        {
          params: {
            includeAncestors: 'true',
            activeOnly: 'true',
          },
        }
      );

      if (isMountedRef.current) {
        const deployments = data.deployments || [];
        const targetSphereId = data.directSphereId || sphereId;

        const direct = deployments.filter((d) => d.sphereId === targetSphereId);
        const inherited = deployments.filter((d) => d.sphereId !== targetSphereId);

        setDirectDeployments(direct);
        setInheritedDeployments(inherited);
      }
    } catch (err: unknown) {
      if (isMountedRef.current) {
        setError(
          err instanceof Error ? err.message : 'Failed to fetch deployed approaches'
        );
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [apiClients, sphereId]);

  // Fetch on mount and when sphereId changes
  useEffect(() => {
    if (!apiClients || !sphereId) {
      setDirectDeployments([]);
      setInheritedDeployments([]);
      return;
    }

    fetchDeployments();
  }, [apiClients, sphereId, fetchDeployments]);

  const refetch = useCallback(() => {
    refetchRef.current += 1;
    fetchDeployments();
  }, [fetchDeployments]);

  return { directDeployments, inheritedDeployments, loading, error, refetch };
}
